import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  HostListener,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import currency from "currency.js";
import { FormsModule } from "@angular/forms";
import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";
import { ForceSignPipe } from "../../../shared/form/force-sign.pipe";
import { CurrencyPipe } from "../../../shared/currencies/currency.pipe";
import { CurrencyRoundPipe } from "../../../shared/currencies/currency-round.pipe";

@Component({
  selector: "app-price-box",
  templateUrl: "./price-box.component.html",
  styleUrls: ["./price-box.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    Bind,
    InputText,
    CurrencyPipe,
    ForceSignPipe,
    CurrencyRoundPipe,
  ],
})
export class PriceBoxComponent implements OnChanges {
  public readonly netChange = output<number>();
  public readonly marginChange = output<number>();
  public readonly save = output();

  public readonly editable = input(true);
  public readonly nullable = input(false);
  public readonly displayKgAsTon = input(false);
  public readonly displayMargin = input(false);
  public readonly isNetDisabled = input(true);

  @Input() public net: number;
  @Input() public margin: number;
  public readonly multiplier = input<number>(undefined);
  public total: number;
  public multipliedTotal: number;

  private netCache = 0;
  private marginCache = 0;

  private sanitize(value: any): string | number {
    if (typeof value === "string" && value.includes(",")) {
      return value.replace(",", ".");
    }
    return value;
  }

  constructor() {}

  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (event.code === "KeyA" && (event.metaKey || event.ctrlKey)) {
      event.stopImmediatePropagation();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.net) {
      this.net = this.displayKgAsTon()
        ? changes.net.currentValue * 1000
        : changes.net.currentValue;
    }
    if (changes.margin) {
      this.margin = this.displayKgAsTon()
        ? changes.margin.currentValue * 1000
        : changes.margin.currentValue;
    }
    this.calculateTotals();
  }

  public toggleNullableNet() {
    if (!this.isNull()) {
      this.netCache = this.net;
      this.net = null;
      this.margin = null;
    } else {
      this.net = this.netCache;
      this.margin = this.marginCache;
    }
    this.calculateTotals();
    this.marginValueChanged();
    this.netValueChanged();
  }

  public isNull() {
    return this.nullable() && this.net === null;
  }

  public shouldDisplayDash() {
    const displayMargin = this.displayMargin();
    return (
      this.isNull() ||
      (!this.isNull() && !displayMargin && this.net === 0) ||
      (!this.isNull() && displayMargin && this.total === 0)
    );
  }

  public isMarginAndTotalDisabled() {
    return !this.isNetDisabled() && this.net === 0;
  }

  public calculateTotals() {
    this.total = currency(this.sanitize(this.net), { precision: 6 }).add(
      this.sanitize(this.margin),
    ).value;
    if (isNaN(this.total)) {
      this.total = 0;
    }
    this.multipliedTotal = currency(this.total, { precision: 6 }).multiply(
      this.multiplier(),
    ).value;
  }

  public calculateMarginFromTotal() {
    this.margin = currency(this.sanitize(this.total), {
      precision: 6,
    }).subtract(this.sanitize(this.net)).value;
    this.marginValueChanged();
  }

  public calculateMarginFromMultipliedTotal() {
    this.total = currency(this.sanitize(this.multipliedTotal), {
      precision: 6,
    }).divide(this.multiplier()).value;
    this.margin = currency(this.total, { precision: 6 }).subtract(
      this.sanitize(this.net),
    ).value;
    this.marginValueChanged();
  }

  public netValueChanged() {
    this.net = Number(this.sanitize(this.net));
    if (this.displayKgAsTon()) {
      this.netChange.emit(this.net / 1000);
    } else {
      this.netChange.emit(this.net);
    }
  }

  public parseNewMargin(newMargin) {
    newMargin = this.sanitize(newMargin);
    if (!isNaN(newMargin) && newMargin !== null) {
      return newMargin;
    } else {
      return this.margin;
    }
  }

  public marginValueChanged() {
    if (this.displayKgAsTon()) {
      this.marginChange.emit(this.margin / 1000);
    } else {
      this.marginChange.emit(this.margin);
    }
  }

  public saveEmit() {
    this.save.emit();
  }
}
